'use client';

import { AdBanner } from '@/clients/customer/ad-banner';
import Image from 'next/image';
import { pushEvent } from '@/lib/utils/functions';

type ImageData = {
  url: string;
} | null;

type AdBannerImageProps = {
  mobileImage?: ImageData;
  desktopImage?: ImageData;
  alt?: string;
  className?: string;
  link?: string;
  adBanner?: AdBanner;
  location?: 'homepage' | 'tags_list';
  tagName?: string;
};

export default function AdBannerImage({
  mobileImage,
  desktopImage,
  alt = 'Ad Banner',
  className = '',
  link,
  adBanner,
  location,
  tagName,
}: AdBannerImageProps) {
  const hasMobileImage = !!mobileImage?.url;
  const hasDesktopImage = !!desktopImage?.url;

  // Don't render anything if neither image is available
  if (!hasMobileImage && !hasDesktopImage) return null;

  const BannerWrapper = ({ children }: { children: React.ReactNode }) => {
    if (link) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="ad-banner-track flex justify-center"
          onClick={() => {
            pushEvent({
              event: 'banner_click',
              eventOptions: {
                banner_type: 'ad_banner',
                banner_id: adBanner?.id,
                shop_name: adBanner?.shop,
                banner_link: link,
                banner_location: location,
                tag_name: tagName ?? 'none',
              },
            });
          }}
        >
          {children}
        </a>
      );
    }
    return <>{children}</>;
  };

  return (
    <div className={className} data-tag-id={adBanner?.id || undefined}>
      {/* Mobile Ad Banner - Only show if mobile_image is available */}
      {hasMobileImage && (
        <BannerWrapper>
          <div
            className={`w-full min-w-[300px] lg:hidden ${
              link ? 'cursor-pointer' : ''
            }`}
          >
            <Image
              src={mobileImage.url}
              alt={alt}
              width={300}
              height={250}
              className="h-auto w-full object-contain"
            />
          </div>
        </BannerWrapper>
      )}

      {/* Desktop Ad Banner - Only show if desktop_image is available */}
      {hasDesktopImage && (
        <BannerWrapper>
          <div
            className={`hidden min-w-[970px] max-w-[970px] lg:block ${
              link ? 'cursor-pointer' : ''
            }`}
          >
            <Image
              src={desktopImage.url}
              alt={alt}
              width={970}
              height={250}
              className="h-auto w-full object-contain"
            />
          </div>
        </BannerWrapper>
      )}
    </div>
  );
}
